import { AspectRatio, Box, Card, H3, Typography } from '@vp/swan'
import { useEffect, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import { produce } from 'immer'
import { TextEditor, UploadsModal, UploadsModalOpenButton } from '../components'
import { SCHEME_KEYS, usePersonalizationManager } from '../PersonalizationManager'
import { useSchemeActions } from '../../Schemes/hooks/useSchemeActions'
import { RenderableTextField } from '../../Schemes/store/types'
import { useUploadsStore } from '../UploadsProvider'
import { useSchemeUploads } from '../../Schemes/hooks/useSchemeUploads'
import { useSchemeTemplateText } from '../../Schemes/hooks/useSchemeTemplateText'

export const PersonalizationCard = () => {
  const { isInitialized, uploadModalOpen, setUploadModalOpen, translations, tracking } = usePersonalizationManager()
  const { cardCopy } = translations
  const { selectedUploads } = useUploadsStore()

  const { activate: activateManualScheme, update: updateManualScheme } = useSchemeActions(SCHEME_KEYS.MANUAL)
  const { templateTextFields } = useSchemeTemplateText(SCHEME_KEYS.MANUAL)
  const { uploads } = useSchemeUploads(SCHEME_KEYS.MANUAL)
  const [textFields, setTextFields] = useState<RenderableTextField[]>([])

  const onTextChangeSettled = useDebouncedCallback((updatedFields: RenderableTextField[]) => {
    updateManualScheme(uploads, updatedFields)
    activateManualScheme()
    updatedFields.forEach((textField) => {
      if (textField.isModified) {
        tracking.textPersonalized(textField.purposeName)
      }
    })
    setTextFields((prevState) =>
      produce(prevState, (draft) =>
        draft.forEach((field) => {
          // // eslint-disable-next-line no-param-reassign
          field.isModified = false
        }),
      ),
    )
  }, 700)

  const onTextChange = (index: number, value: string): void => {
    setTextFields((prevState) => {
      const updatedFields = produce(prevState, (draft) => {
        draft[index].text = value
        draft[index].isModified = true
      })

      onTextChangeSettled(updatedFields)
      return updatedFields
    })
  }

  const handleUploadModalSubmit = (): void => {
    tracking.modalImagesApplied(selectedUploads.length)
    updateManualScheme(selectedUploads, [])
    activateManualScheme()
    setUploadModalOpen(false)
  }

  const handleSignIn = async (): Promise<void> => {
    await updateManualScheme(selectedUploads, [])
  }

  useEffect(() => {
    if (isInitialized) {
      setTextFields(templateTextFields)
      tracking.sidebarViewed()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized])

  return (
    <Card bgc="accent" my="between-subsections" p={6}>
      <H3 fontSkin="title-subsection" mb={2}>
        {cardCopy.filterHeader}
      </H3>
      <Typography fontSkin="body-small" mb={6}>
        {cardCopy.filterSubtext}
      </Typography>
      {isInitialized ? (
        <>
          <Box mb="between-actions" pt={0}>
            <UploadsModalOpenButton onClick={(): void => setUploadModalOpen(true)} />
          </Box>
          <TextEditor showLoader setTextFields={setTextFields} textFields={textFields} onTextChange={onTextChange} />
        </>
      ) : (
        <AspectRatio loadingShimmer ratio={0.6} />
      )}
      <UploadsModal handleSignIn={handleSignIn} handleSubmit={handleUploadModalSubmit} isOpen={uploadModalOpen} setIsOpen={setUploadModalOpen} />
    </Card>
  )
}
